import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Typography, Result, Spin, Alert,
    Row, Col, Statistic, Tag, Table, Collapse,
    Divider, Space, Badge,
} from 'antd';
import {
    InfoCircleOutlined, TrophyOutlined,
    StarOutlined, RiseOutlined, BookOutlined,
} from '@ant-design/icons';
import UserLayout from '../Layout/UserLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Title, Text } = Typography;

// ── Hằng số hiển thị ─────────────────────────────────────────────────────────

const AREA_LABEL = {
    KV1:     'Khu vực 1 (KV1)',
    'KV2-NT':'Khu vực 2 nông thôn (KV2-NT)',
    KV2:     'Khu vực 2 (KV2)',
    KV3:     'Khu vực 3 (KV3)',
};

const COMBO_COLOR = { A: 'blue', B: 'green', C: 'volcano', D: 'purple', X: 'orange' };
const getComboColor = (code = '') => COMBO_COLOR[code[0]] ?? 'default';

const SUBJECT_LABEL = {
    math:            'Toán',
    literature:      'Ngữ văn',
    english:         'Ngoại ngữ',
    physics:         'Vật lý',
    chemistry:       'Hóa học',
    biology:         'Sinh học',
    history:         'Lịch sử',
    geography:       'Địa lý',
    civic_education: 'GDCD',
    technology:      'Công nghệ',
    computer_science:'Tin học',
};

// ── Bảng điểm thi THPT ───────────────────────────────────────────────────────

const ThptScoreTable = ({ thptScores }) => {
    if (!thptScores) return null;

    const subjects = Object.entries(thptScores);
    const hasAnyScore = subjects.some(([, v]) => v !== null);
    if (!hasAnyScore) return null;

    const dataRow = { key: 'row' };
    subjects.forEach(([key, val]) => {
        dataRow[key] = val !== null ? parseFloat(val) : null;
    });

    const columns = subjects.map(([key]) => ({
        title: SUBJECT_LABEL[key] ?? key,
        key,
        dataIndex: key,
        align: 'center',
        width: 90,
        render: (v) =>
            v !== null && v !== 0
                ? <Text strong style={{ color: '#1890ff' }}>{v.toFixed(2)}</Text>
                : <Text type="secondary">—</Text>,
    }));

    return (
        <Card
            size="small"
            title="Điểm thi THPT quốc gia"
            style={{ marginBottom: 24 }}
        >
            <Table
                dataSource={[dataRow]}
                columns={columns}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
            />
        </Card>
    );
};

// ── Component điểm cộng ───────────────────────────────────────────────────────

const BonusRow = ({ label, value, icon }) =>
    value > 0 ? (
        <Row justify="space-between" style={{ marginBottom: 4 }}>
            <Col>
                <Space>
                    {icon}
                    <Text>{label}</Text>
                </Space>
            </Col>
            <Col>
                <Text strong style={{ color: '#52c41a' }}>+{value.toFixed(2)}</Text>
            </Col>
        </Row>
    ) : null;

// ── Component tổ hợp của 1 nguyện vọng ───────────────────────────────────────

const AdmissionCard = ({ admission, bonusTotal }) => {
    const hasCombos    = admission.combinations?.length > 0;
    const hasScore     = admission.best_score !== null;
    const noCombosSet  = hasCombos && admission.combinations.every(c => c.total_score === null);

    const columns = [
        {
            title: 'Tổ hợp',
            dataIndex: 'code',
            width: 150,
            render: (code, row) => (
                <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                    <Tag color={getComboColor(code)} style={{ minWidth: 44, textAlign: 'center', marginRight: 0 }}>
                        {code}
                    </Tag>
                    {code === admission.best_combo && (
                        <Text type="success" style={{ fontSize: 11 }}>★ Cao nhất</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Môn thi',
            dataIndex: 'subjects',
            render: (subjects) => subjects?.join(' – '),
        },
        ...(admission.benchmark ? [{
            title: `Điểm chuẩn ${admission.benchmark.year}`,
            key: 'benchmark',
            width: 110,
            align: 'center',
            render: () => (
                <Text style={{ color: '#595959' }}>
                    {admission.benchmark.cutoff_score.toFixed(2)}
                </Text>
            ),
        }] : []),
        {
            title: 'Điểm THM',
            dataIndex: 'thm_score',
            width: 100,
            align: 'center',
            render: (v, row) =>
                row.note
                    ? <Text type="secondary" style={{ fontSize: 12 }}>{row.note}</Text>
                    : <Text strong>{v?.toFixed(2)}</Text>,
        },
        {
            title: 'Điểm xét tuyển',
            dataIndex: 'total_score',
            width: 130,
            align: 'center',
            render: (v, row) => {
                if (row.note || v === null) return <Text type="secondary">—</Text>;
                const isBest = row.code === admission.best_combo;
                return (
                    <Text
                        strong
                        style={{ fontSize: isBest ? 16 : 14, color: isBest ? '#1890ff' : undefined }}
                    >
                        {v?.toFixed(2)}
                    </Text>
                );
            },
        },
    ];

    return (
        <Card
            size="small"
            style={{ marginBottom: 16, borderLeft: `4px solid ${hasScore ? '#1890ff' : '#d9d9d9'}` }}
            styles={{ header: { padding: '8px 12px' } }}
            title={
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 16px' }}>
                    <Space size={4} style={{ flex: '1 1 auto', minWidth: 0, flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: 15, whiteSpace: 'nowrap' }}>
                            Nguyện vọng {admission.priority}:
                        </Text>
                        <Text style={{ wordBreak: 'break-word' }}>{admission.subject_name}</Text>
                        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>({admission.subject_code})</Text>
                    </Space>
                    {hasScore ? (
                        <Space size={4} style={{ flex: '0 0 auto' }}>
                            <Text type="secondary" style={{ fontSize: 13 }}>Điểm cao nhất:</Text>
                            <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                                {admission.best_score?.toFixed(2)}
                            </Text>
                            <Tag color={getComboColor(admission.best_combo)}>{admission.best_combo}</Tag>
                        </Space>
                    ) : (
                        <Text type="secondary" style={{ flex: '0 0 auto', fontSize: 13 }}>Chưa đủ dữ liệu điểm</Text>
                    )}
                </div>
            }
        >
            {!hasCombos ? (
                <Alert
                    type="warning"
                    showIcon
                    message="Ngành này chưa được gán tổ hợp xét tuyển. Vui lòng liên hệ nhà trường."
                />
            ) : noCombosSet ? (
                <Alert
                    type="warning"
                    showIcon
                    message="Thiếu dữ liệu điểm một hoặc nhiều môn trong tổ hợp. Vui lòng bổ sung điểm học bạ."
                />
            ) : (
                <>
                    <Collapse
                        ghost
                        size="small"
                        items={[{
                            key: '1',
                            label: <Text type="secondary">Chi tiết điểm theo từng tổ hợp</Text>,
                            children: (
                                <Table
                                    dataSource={admission.combinations}
                                    columns={columns}
                                    rowKey="code"
                                    size="small"
                                    pagination={false}
                                    rowClassName={(row) =>
                                        row.code === admission.best_combo ? 'ant-table-row-selected' : ''
                                    }
                                />
                            ),
                        }]}
                    />
                    <Row style={{ marginTop: 8, padding: '6px 12px', background: '#fafafa', borderRadius: 6 }}>
                        <Col flex="auto">
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Điểm THM tốt nhất ({admission.best_combo}): <strong>{admission.best_thm?.toFixed(2)}</strong>
                                {'  +  '}Điểm cộng: <strong>+{bonusTotal.toFixed(2)}</strong>
                                {'  +  '}Điểm ưu tiên: <strong>+{(admission.best_effective_priority ?? 0).toFixed(2)}</strong>
                            </Text>
                        </Col>
                        <Col>
                            <Text strong style={{ color: '#1890ff' }}>
                                = {admission.best_score?.toFixed(2)}
                            </Text>
                        </Col>
                    </Row>
                </>
            )}
        </Card>
    );
};

// ── Component chính ───────────────────────────────────────────────────────────

const ResultLookup = () => {
    const [loading, setLoading]   = useState(true);
    const [scoreData, setScoreData] = useState(null);
    const [error, setError]       = useState(null);

    const fetchScore = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/v1/my-score');
            if (res.data?.success) {
                setScoreData(res.data.data);
            } else {
                setError(res.data?.message ?? 'Không thể tải điểm.');
            }
        } catch (err) {
            const msg = err.response?.data?.message;
            const code = err.response?.data?.error_code;
            if (code === 'PROFILE_NOT_FOUND') {
                setError('Chưa có hồ sơ thí sinh. Vui lòng hoàn thiện hồ sơ trước.');
            } else if (code === 'NO_ADMISSIONS') {
                setError('Chưa có nguyện vọng xét tuyển. Vui lòng đăng ký nguyện vọng trước.');
            } else {
                setError(msg ?? 'Xảy ra lỗi khi tải điểm. Vui lòng thử lại sau.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchScore(); }, [fetchScore]);

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spin size="large" tip="Đang tính điểm xét tuyển..." />
                </div>
            );
        }

        if (error) {
            return (
                <Result
                    icon={<InfoCircleOutlined style={{ color: '#faad14' }} />}
                    title="Chưa thể tra cứu điểm"
                    subTitle={error}
                />
            );
        }

        if (!scoreData) return null;

        const { bonus_points: bp, priority_points: pp } = scoreData;
        const hasBonusOrPriority = bp.total > 0 || pp.total > 0;

        return (
            <>
                {/* ── Điểm thi THPT quốc gia ── */}
                <ThptScoreTable thptScores={scoreData.thpt_exam_scores} />

                {/* ── Thông tin tổng quan ── */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {/* Thông tin thí sinh */}
                    <Col xs={24} md={10}>
                        <Card size="small" title={<><BookOutlined /> Thông tin thí sinh</>}>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 14 }}>
                                        Số CCCD: {scoreData.full_name ?? '—'}
                                    </Text>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: 14 }}>
                                        Số CCCD: {scoreData.identification ?? '—'}
                                    </Text>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {/* Điểm cộng */}
                    <Col xs={24} md={7}>
                        <Card
                            size="small"
                            title={<><TrophyOutlined style={{ color: '#faad14' }} /> Điểm cộng</>}
                            extra={
                                <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                                    +{bp.total.toFixed(2)}
                                </Text>
                            }
                        >
                            {!hasBonusOrPriority && bp.total === 0 ? (
                                <Text type="secondary">Không có điểm cộng</Text>
                            ) : (
                                <>
                                    <BonusRow
                                        label="Thưởng IELTS"
                                        value={bp.ielts}
                                        icon={<StarOutlined style={{ color: '#1890ff' }} />}
                                    />
                                    <BonusRow
                                        label="Thưởng giải HSG"
                                        value={bp.hsg_prize}
                                        icon={<TrophyOutlined style={{ color: '#faad14' }} />}
                                    />
                                    <BonusRow
                                        label="Thưởng học lực Tốt/Giỏi"
                                        value={bp.academic_ranking}
                                        icon={<RiseOutlined style={{ color: '#52c41a' }} />}
                                    />
                                    {bp.total === 0 && (
                                        <Text type="secondary">Không có điểm cộng</Text>
                                    )}
                                </>
                            )}
                        </Card>
                    </Col>

                    {/* Điểm ưu tiên */}
                    <Col xs={24} md={7}>
                        <Card
                            size="small"
                            title={<><RiseOutlined style={{ color: '#722ed1' }} /> Điểm ưu tiên</>}
                            extra={
                                <Text strong style={{ color: '#722ed1', fontSize: 16 }}>
                                    +{pp.total.toFixed(2)}
                                </Text>
                            }
                        >
                            <BonusRow label="Khu vực" value={pp.area} />
                            <BonusRow label="Đối tượng" value={pp.group} />
                            {pp.total === 0 && (
                                <Text type="secondary">Không có điểm ưu tiên</Text>
                            )}
                        </Card>
                    </Col>
                </Row>

                <Divider orientation="left">
                    <Text strong>Điểm xét tuyển theo nguyện vọng</Text>
                </Divider>

                {/* ── Danh sách nguyện vọng ── */}
                {scoreData.admissions.length === 0 ? (
                    <Alert type="warning" showIcon message="Chưa có nguyện vọng xét tuyển." />
                ) : (
                    scoreData.admissions.map((admission) => (
                        <AdmissionCard
                            key={admission.priority}
                            admission={admission}
                            bonusTotal={bp.total}
                        />
                    ))
                )}

                <Alert
                    style={{ marginTop: 16 }}
                    type="info"
                    showIcon
                    message="Lưu ý"
                    description={
                        'Điểm xét tuyển = Điểm tổ hợp môn (THM) + Điểm cộng + Điểm ưu tiên. ' +
                        'THM = trung bình điểm 3 năm lớp 10, 11, 12 của từng môn trong tổ hợp. ' +
                        'Điểm hiển thị mang tính tham khảo dựa trên dữ liệu đã khai báo.'
                    }
                />
            </>
        );
    };

    return (
        <UserLayout title="TRA CỨU KẾT QUẢ">
            {/* <Title level={4} style={{ marginBottom: 16 }}>
                Điểm xét tuyển theo phương thức xét kết quả học tập THPT
            </Title> */}
            {renderContent()}
        </UserLayout>
    );
};

export default ResultLookup;
